<script lang="ts">
import { ref } from 'vue';

/** Bumped whenever a Tier II review step is marked complete/reopened, to refresh the tracker. */
export const tierIIStatusUpdateTrigger = ref(0);
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { watchAndFetch } from '../../../cross-app-frontend/watch-and-fetch';
import { actions } from '@/services/actions';
import { Popover, PopoverContent, PopoverTrigger } from '../../../cross-app-frontend/popover';
import { Button } from '../../../cross-app-frontend/button';
import type { FacilityID } from '../../../tri-shared/types/db';
import {
  TIERII_WORKFLOW_STATUS_DEFAULT,
  tierIIWorkflowPercentage,
  tierIIWorkflowSteps,
} from '../services/workflow-progress';

const props = defineProps<{
  facilityID: FacilityID;
  facilitySlug: string;
  year: number;
}>();

const { loading, response: status } = watchAndFetch(
  computed(() => ({
    facilityID: props.facilityID,
    year: props.year,
    trigger: tierIIStatusUpdateTrigger.value,
  })),
  TIERII_WORKFLOW_STATUS_DEFAULT,
  async ({ facilityID, year }) => actions.tierii.tierIISubmissionStatusGet({ facilityID, year }),
);

const facilitySlug = computed(() => props.facilitySlug);

const stages = computed(() => tierIIWorkflowSteps(status.value));
const overallPercentage = computed(() => tierIIWorkflowPercentage(stages.value));

const circleCircumference = 2 * Math.PI * 20; // radius = 20
const strokeDashoffset = computed(() => {
  const progress = Math.max(overallPercentage.value / 100, 0.02);
  return circleCircumference * (1 - progress);
});

const popoverOpen = ref(false);
</script>

<template>
  <div class="relative">
    <Popover v-model:open="popoverOpen">
      <PopoverTrigger asChild>
        <div class="group relative h-12 w-12 cursor-pointer">
          <!-- Progress ring -->
          <svg class="absolute inset-0 h-12 w-12 -rotate-90 transform" viewBox="0 0 48 48">
            <circle cx="24" cy="24" r="20" stroke="#e5e7eb" stroke-width="4" fill="none" />
            <circle
              cx="24"
              cy="24"
              r="20"
              stroke="#5700ca"
              stroke-width="6"
              fill="none"
              :stroke-dasharray="circleCircumference"
              :stroke-dashoffset="strokeDashoffset"
              stroke-linecap="round"
              class="transition-all duration-300 ease-in-out"
            />
          </svg>

          <div
            class="absolute inset-1 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-2 border-gray-300 bg-white p-0 shadow-xs transition-all duration-200 hover:bg-gray-50"
            :class="{ 'opacity-75': loading }"
            :title="`Tier II Progress: ${overallPercentage}% complete`"
          >
            <i v-if="loading" class="fa fa-spin fa-spinner-third text-xs text-gray-500"></i>
            <span v-else class="text-xs font-bold text-gray-700">{{ overallPercentage }}%</span>
          </div>
        </div>
      </PopoverTrigger>

      <PopoverContent class="w-80 border-0 p-0 shadow-lg" align="end">
        <div class="rounded-lg bg-white p-4">
          <div class="mb-4 border-b border-gray-100 pb-3">
            <h3 class="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <div class="bg-brand-purple h-2 w-2 rounded-full"></div>
              Tier II Progress
            </h3>
            <p class="mt-1 text-sm text-gray-600">
              {{ stages.filter((s) => s.isDone).length }} of {{ stages.length }} steps completed
              <span class="text-brand-purple font-medium">({{ overallPercentage }}%)</span>
            </p>
          </div>

          <div class="space-y-3">
            <RouterLink
              v-for="stage of stages"
              :key="stage.route"
              class="group flex cursor-pointer items-center gap-3 rounded-lg border border-transparent p-3 transition-all duration-200 hover:border-purple-100 hover:bg-purple-50"
              :to="{ name: stage.route, params: { facilityID, facilitySlug, year } }"
              @click="popoverOpen = false"
            >
              <div
                class="flex h-8 w-8 items-center justify-center rounded-full transition-all duration-200"
                :class="
                  stage.isDone ? 'bg-brand-purple ring-brand-purple text-white ring-2' : 'bg-gray-100 text-gray-400'
                "
              >
                <i class="fa-light text-sm" :class="stage.icon"></i>
              </div>

              <div class="min-w-0 flex-1">
                <h4 class="truncate text-sm font-medium text-gray-900">{{ stage.title }}</h4>
                <p class="truncate text-xs text-gray-500">{{ stage.description }}</p>
              </div>

              <div class="shrink-0">
                <i
                  class="fa-light fa-chevron-right text-sm text-gray-400 transition-transform duration-200 group-hover:translate-x-1"
                ></i>
              </div>
            </RouterLink>
          </div>

          <div class="mt-4 border-t border-gray-100 pt-3">
            <Button
              variant="outline"
              size="sm"
              class="w-full transition-colors duration-200 hover:border-purple-200 hover:bg-purple-50"
              :as="RouterLink"
              :to="{ name: 'TierII Overview', params: { facilityID, facilitySlug } }"
              @click="popoverOpen = false"
            >
              <i class="fa-light fa-chart-line text-brand-purple mr-2"></i>
              <span class="text-brand-purple font-medium">View Overview</span>
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  </div>
</template>
